// Ticket 29: Elite Perks Page
import React, { useEffect, useState } from 'react';
import { fetchEliteStatus } from '../api/elite';
import { ArrowLeft, Crown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ElitePerksPage = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    const loadStatus = async () => {
      try {
        setLoading(true);
        const data = await fetchEliteStatus();
        if (!cancelled) {
          setStatus(data);
        }
      } catch (err) {
        console.error('[Elite] Failed to load status:', err);
        if (!cancelled) {
          setError('Unable to load Elite status');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadStatus();

    return () => {
      cancelled = true;
    };
  }, []);

  const isElite = status?.is_elite || false;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950/30 to-slate-950">
      <div className="mx-auto flex h-full max-w-md flex-col px-4 py-4">
        {/* Header */}
        <div className="mb-4 flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-900/60 text-slate-300 hover:bg-slate-800 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-lg font-playfair font-semibold text-slate-50">
            Elite Membership
          </h1>
        </div>

        {loading && !status && (
          <p className="text-xs text-slate-400">Loading...</p>
        )}
        
        {error && (
          <p className="text-xs text-red-300">{error}</p>
        )}

        {status && (
          <>
            {/* Elite Status Card */}
            <div className="mb-3 rounded-2xl bg-gradient-to-r from-amber-400 via-pink-500 to-purple-600 p-[1px]">
              <div className="rounded-2xl bg-slate-950 px-4 py-4">
                <div className="flex items-center gap-2 mb-2">
                  <Crown className="w-5 h-5 text-amber-400" />
                  <p className="text-sm font-semibold text-slate-50">
                    {isElite ? "You're Elite!" : "Unlock Elite"}
                  </p>
                </div>
                <p className="text-[11px] text-slate-200 mb-3">
                  {isElite
                    ? 'Enjoy your perks, monthly LoveCoins, and exclusive gifts.'
                    : 'Upgrade to Elite to unlock exclusive gifts, monthly LoveCoins, and priority status across the app.'}
                </p>
                <div className="rounded-xl bg-slate-900/60 px-3 py-2 mb-3">
                  <p className="text-[11px] text-pink-300">
                    Monthly bonus:{' '}
                    <span className="font-semibold text-pink-200">
                      +{status.monthly_bonus_coins} LoveCoins
                    </span>
                  </p>
                </div>
                {!isElite && (
                  <button
                    className="w-full rounded-full bg-gradient-rose-gold py-2.5 text-xs font-semibold text-white hover:opacity-90 transition-opacity"
                    onClick={() => navigate('/premium')}
                  >
                    Upgrade to Elite
                  </button>
                )}
              </div>
            </div>

            {/* Perks List */}
            <div className="mb-3 rounded-2xl bg-white px-4 py-3 text-xs text-slate-800 shadow-sm">
              <p className="mb-2 text-[11px] font-semibold text-slate-700">
                Your Perks
              </p>
              <ul className="space-y-2 text-[11px]">
                {status.perks?.map((perk, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-slate-700">
                    <span className="text-emerald-600 font-bold">✓</span>
                    <span className="flex-1">{perk}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Elite Gifts */}
            {status.elite_gifts && status.elite_gifts.length > 0 && (
              <div className="rounded-2xl bg-white px-4 py-3 text-xs text-slate-800 shadow-sm">
                <p className="mb-2 text-[11px] font-semibold text-slate-700">
                  Elite-Exclusive Gifts
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {status.elite_gifts.map((gift) => (
                    <div
                      key={gift.key}
                      className="flex flex-col items-center rounded-2xl bg-gradient-to-br from-amber-50 to-purple-50 border border-amber-200 px-2 py-3"
                    >
                      <span className="mb-1 text-2xl">{gift.emoji || '🎁'}</span>
                      <span className="line-clamp-1 text-[10px] text-slate-800 text-center font-medium">
                        {gift.name}
                      </span>
                      <span className="text-[9px] text-amber-600 font-semibold">
                        {gift.cost}c
                      </span>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-[10px] text-slate-600 text-center">
                  These premium gifts are only available to Elite members
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ElitePerksPage;
